import { useState } from 'react';
import type { Integration, IntegrationType, CreateIntegrationDto, UpdateIntegrationDto } from '@yawi/types';
import {
  Plug,
  RefreshCw,
  Plus,
  Mail,
  Smartphone,
  MessageSquare,
  Bell,
  CreditCard,
  Webhook,
  ScrollText,
} from 'lucide-react';
import { Button, Card } from '@/core/ui/components';
import { useIntegrations } from '../hooks/useIntegrations';
import { useWebhooks } from '../hooks/useWebhooks';
import { useIntegrationLogs } from '../hooks/useIntegrationLogs';
import { IntegrationList } from '../components/IntegrationList';
import { IntegrationWizard } from '../components/wizard';
import { WebhookManager } from '../components/WebhookManager';
import { IntegrationLogs } from '../components/IntegrationLogs';

type TabType = 'integrations' | 'webhooks' | 'logs';

const tabs: { id: TabType; label: string; icon: React.ReactNode }[] = [
  { id: 'integrations', label: 'Integraciones', icon: <Plug className="w-4 h-4" /> },
  { id: 'webhooks', label: 'Webhooks', icon: <Webhook className="w-4 h-4" /> },
  { id: 'logs', label: 'Logs', icon: <ScrollText className="w-4 h-4" /> },
];

export function IntegrationsPage() {
  const {
    integrations,
    isLoading,
    isSaving,
    isTesting,
    createIntegration,
    updateIntegration,
    deleteIntegration,
    testIntegration,
    enableIntegration,
    disableIntegration,
    refresh,
  } = useIntegrations();

  const {
    webhooks,
    isLoading: isLoadingWebhooks,
    isSaving: isSavingWebhook,
    createWebhook,
    updateWebhook,
    deleteWebhook,
    testWebhook,
  } = useWebhooks();

  const {
    logs,
    isLoading: isLoadingLogs,
    totalCount: logsCount,
    filters: logFilters,
    setFilters: setLogFilters,
    refresh: refreshLogs,
  } = useIntegrationLogs();

  const [activeTab, setActiveTab] = useState<TabType>('integrations');
  const [showForm, setShowForm] = useState(false);
  const [editingIntegration, setEditingIntegration] = useState<Integration | null>(null);

  // Count active integrations
  const activeIntegrations = integrations.filter(i => i.status === 'ENABLED').length;
  const activeWebhooks = webhooks.filter(w => w.status === 'ACTIVE').length;

  const handleCreate = () => {
    setEditingIntegration(null);
    setShowForm(true);
  };

  const handleEdit = (integration: Integration) => {
    setEditingIntegration(integration);
    setShowForm(true);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingIntegration(null);
  };

  const handleSave = async (data: CreateIntegrationDto | UpdateIntegrationDto, isNew: boolean) => {
    if (isNew) {
      await createIntegration(data as CreateIntegrationDto);
    } else if (editingIntegration) {
      await updateIntegration(editingIntegration.id, data as UpdateIntegrationDto);
    }
    handleCloseForm();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 dark:text-white/90 flex items-center gap-3">
            <Plug className="w-7 h-7" />
            Integraciones
          </h1>
          <p className="mt-1 text-gray-500 dark:text-gray-400">
            Configura proveedores de notificaciones y pagos con fallback automático
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={refresh}
            leftIcon={<RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />}
          >
            Actualizar
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleCreate}
            leftIcon={<Plus className="w-4 h-4" />}
          >
            Nueva Integración
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-rose-100 dark:bg-rose-900/30 rounded-lg">
              <Mail className="w-5 h-5 text-rose-600 dark:text-rose-400" />
            </div>
            <div>
              <div className="text-sm text-gray-500 dark:text-gray-400">Email</div>
              <div className="text-xl font-bold text-gray-800 dark:text-white/90">
                {integrations.filter(i => i.type === 'EMAIL' && i.status === 'ENABLED').length}
              </div>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg">
              <Smartphone className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
            </div>
            <div>
              <div className="text-sm text-gray-500 dark:text-gray-400">SMS</div>
              <div className="text-xl font-bold text-gray-800 dark:text-white/90">
                {integrations.filter(i => i.type === 'SMS' && i.status === 'ENABLED').length}
              </div>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
              <MessageSquare className="w-5 h-5 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <div className="text-sm text-gray-500 dark:text-gray-400">WhatsApp</div>
              <div className="text-xl font-bold text-gray-800 dark:text-white/90">
                {integrations.filter(i => i.type === 'WHATSAPP' && i.status === 'ENABLED').length}
              </div>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
              <Bell className="w-5 h-5 text-amber-600 dark:text-amber-400" />
            </div>
            <div>
              <div className="text-sm text-gray-500 dark:text-gray-400">Push</div>
              <div className="text-xl font-bold text-gray-800 dark:text-white/90">
                {integrations.filter(i => i.type === 'PUSH' && i.status === 'ENABLED').length}
              </div>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-violet-100 dark:bg-violet-900/30 rounded-lg">
              <CreditCard className="w-5 h-5 text-violet-600 dark:text-violet-400" />
            </div>
            <div>
              <div className="text-sm text-gray-500 dark:text-gray-400">Pagos</div>
              <div className="text-xl font-bold text-gray-800 dark:text-white/90">
                {integrations.filter(i => i.type === 'PAYMENT' && i.status === 'ENABLED').length}
              </div>
            </div>
          </div>
        </Card>
      </div>

      {/* Fallback Info */}
      <Card className="p-4 bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800">
        <div className="flex items-start gap-3">
          <div className="p-1.5 bg-blue-100 dark:bg-blue-900/50 rounded">
            <RefreshCw className="w-4 h-4 text-blue-600 dark:text-blue-400" />
          </div>
          <div>
            <h4 className="font-medium text-blue-800 dark:text-blue-200">Sistema de Fallback</h4>
            <p className="text-sm text-blue-600 dark:text-blue-300 mt-0.5">
              Si un proveedor falla, el sistema intenta con el siguiente según prioridad.
              Después de 5 errores consecutivos, se marca como ERROR y se salta automáticamente.
            </p>
          </div>
        </div>
      </Card>

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-800">
        <nav className="flex gap-4">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-brand-500 text-brand-600 dark:text-brand-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'integrations' && (
        <IntegrationList
          integrations={integrations}
          isLoading={isLoading}
          isTesting={isTesting}
          onEdit={handleEdit}
          onDelete={deleteIntegration}
          onTest={testIntegration}
          onEnable={enableIntegration}
          onDisable={disableIntegration}
        />
      )}

      {activeTab === 'webhooks' && (
        <WebhookManager
          webhooks={webhooks}
          isLoading={isLoadingWebhooks}
          isSaving={isSavingWebhook}
          onCreateWebhook={createWebhook}
          onUpdateWebhook={updateWebhook}
          onDeleteWebhook={deleteWebhook}
          onTestWebhook={testWebhook}
        />
      )}

      {activeTab === 'logs' && (
        <IntegrationLogs
          logs={logs}
          isLoading={isLoadingLogs}
          totalCount={logsCount}
          filters={logFilters}
          onFilterChange={setLogFilters}
          onRefresh={refreshLogs}
        />
      )}

      {/* Integration Wizard */}
      {showForm && (
        <IntegrationWizard
          integration={editingIntegration}
          isSaving={isSaving}
          onSave={handleSave}
          onClose={handleCloseForm}
        />
      )}
    </div>
  );
}
